import type {AhpSpaceSelection} from '@github-ui/ahp-session-client/contracts'
import type {ComposerAttachment} from '@github-ui/agent-sessions-interface/components/ChatComposer'

/**
 * Stable id for the composer token that keeps the selected Space visible after
 * the attach ("+") menu closes. The picker lives inside that menu, so its label
 * unmounts the moment a Space is chosen; this token is the persistent indicator
 * rendered in the composer's attachment row instead.
 */
export const SELECTED_SPACE_ATTACHMENT_ID = 'copilot-selected-space'

/**
 * Builds the display-only attachment token for the currently selected Space, or
 * `undefined` when no Space is selected. The token is presentation only — the
 * Space is submitted via metadata, not as an attachment — so it is safe to merge
 * into a composer's display attachments without affecting what gets sent.
 */
export function selectedSpaceAttachment(selectedSpace: AhpSpaceSelection | undefined): ComposerAttachment | undefined {
  if (!selectedSpace) return undefined
  return {
    id: SELECTED_SPACE_ATTACHMENT_ID,
    label: `Space: ${selectedSpace.name}`,
    icon: 'space',
  }
}

/**
 * True when the composer holds at least one attachment that is real submittable
 * content, i.e. something other than the display-only Space token. Selecting a
 * Space must not, on its own, make an otherwise-empty draft submittable, so the
 * submit-enabled check relies on this rather than the raw attachment count.
 */
export function hasSubmittableAttachments(attachments: readonly ComposerAttachment[]): boolean {
  return attachments.some(attachment => attachment.id !== SELECTED_SPACE_ATTACHMENT_ID)
}
